import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useCallback } from "react";
import { toast } from "react-toastify";

import { ApiChatMessage, chatApi, transformApiChatMessage, useApiClient } from "../../api";
import { QUERY_KEYS } from "../../constants";

export const useChatMessages = (chatId?: string) => {
  const apiClient = useApiClient();

  const fetchMessages = useCallback(async () => {
    if (!chatId) return [];

    const response = await chatApi.getChatMessages(apiClient, chatId);
    const apiMessages = response.data as ApiChatMessage[];
    // Transform API messages to app format
    return apiMessages.flatMap(transformApiChatMessage);
  }, [apiClient, chatId]);

  const query = useQuery({
    queryKey: QUERY_KEYS.chatMessages(chatId),
    queryFn: fetchMessages,
    enabled: !!chatId,
  });

  return query;
};

export const useSubmitFeedback = () => {
  const queryClient = useQueryClient();
  const apiClient = useApiClient();

  return useMutation({
    mutationFn: async ({
      chatId,
      messageId,
      score,
      modelUsed,
    }: {
      chatId: string;
      messageId: string;
      score: 1 | -1;
      modelUsed: "CLAUDE" | "GEMINI";
    }) => {
      const response = await chatApi.submitFeedback(apiClient, chatId, messageId, {
        score,
        modelUsed,
      });
      return response.data;
    },
    onSuccess: (_, variables) => {
      // Invalidate and refetch chat messages to get updated feedback
      queryClient.invalidateQueries({
        queryKey: QUERY_KEYS.chatMessages(variables.chatId),
      });
    },
    onError: (error) => {
      console.error("Failed to submit feedback:", error);
      toast.error("Failed to submit feedback");
    },
  });
};
